/**
 * TestVADialog - TEST/DEV affordance.
 *
 * A "Test VA" trigger that opens a small dialog to fire a claim through the
 * same intake pipeline the Voice Agent uses (admin-authenticated
 * `POST /api/claims/test-va-intake/`, no public API key in the frontend).
 *
 * The dialog lets a tester supply an optional consumer email (+ name) so the
 * submission confirmation and evidence/upload-link emails land in their own
 * inbox and the photo-upload flow can be exercised end to end. All fields are
 * optional - a blank submit reuses the previous throwaway behaviour.
 *
 * The product fields are built from the tenant's OWN identification facets
 * (`GET /api/claims/test-va-intake/`), one field per facet, labelled with the
 * tenant's attribute name. They used to be two hardcoded `code_name`s under a
 * label naming one tenant's product, which collected answers no other
 * tenant's catalogue could be narrowed by and left a second tenant with
 * identification enabled unable to test their own intake at all.
 *
 * Gated upstream by the `voice` feature flag (see claims-page.tsx). Safe to
 * delete with the backend `apps.claims.views.test_intake` module.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { FlaskConical } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useTestVAFacets,
  useTestVAIntake,
  type TestVAFacet,
} from '@/admin/hooks/use-claims'

export function TestVADialog() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { mutateAsync: fireTestVA, isPending } = useTestVAIntake()
  // Only asked for once the dialog is opened - this is a test affordance and
  // has no business costing a request on every Claims page load.
  const { data: facetData } = useTestVAFacets(open)
  const facets: TestVAFacet[] = facetData?.facets ?? []

  const fields: FormField[] = [
    {
      type: 'email',
      name: 'email',
      label: t('testVa.emailLabel'),
      placeholder: 'you+test@iwarranty.co',
      hint: t('testVa.emailHint'),
      value: '',
    },
    {
      type: 'text',
      name: 'first_name',
      label: t('testVa.firstNameLabel'),
      value: '',
    },
    {
      type: 'text',
      name: 'last_name',
      label: t('testVa.lastNameLabel'),
      value: '',
    },
    {
      type: 'text',
      name: 'product_query',
      label: t('testVa.productQueryLabel'),
      hint: t('testVa.productQueryHint'),
      value: '',
    },
    // One field per configured facet. The label is the tenant's own attribute
    // name, so nothing here knows what any tenant sells.
    ...facets.map<FormField>((facet) => ({
      type: 'text',
      name: `facet__${facet.code_name}`,
      label: facet.display_name,
      hint: t('testVa.facetHint'),
      value: '',
    })),
  ]

  const { Form, ref: formRef } = useForm(
    { name: 'test-va-form', submit: 'footer', fields },
    async (raw) => {
      const values = raw as Record<string, string | undefined>
      // Only send facets the tester actually filled in - a blank field is
      // "not asked", not "no answer", and must not narrow anything.
      const productFacets: Record<string, string> = {}
      for (const facet of facets) {
        const value = values[`facet__${facet.code_name}`]?.trim()
        if (value) productFacets[facet.code_name] = value
      }
      try {
        const result = await fireTestVA({
          email: values.email?.trim() || undefined,
          first_name: values.first_name?.trim() || undefined,
          last_name: values.last_name?.trim() || undefined,
          product_query: values.product_query?.trim() || undefined,
          product_facets: Object.keys(productFacets).length
            ? productFacets
            : undefined,
        })
        handleOpenChange(false)
        toast.success(
          result.unidentified_product
            ? t('testVa.createdUnidentified', { reference: result.claim_reference })
            : t('testVa.createdIdentified', { reference: result.claim_reference }),
          {
            action: {
              label: t('common.open'),
              onClick: () => navigate(`/claims/${result.claim_uuid}`),
            },
          },
        )
      } catch (err) {
        toastApiError(err, t('testVa.failed'))
      }
    },
    // The facet code_names decide the field set, so the memo has to rebuild
    // when they arrive; `fields` itself serialises identically either way.
    [fireTestVA, navigate, t, facets.map((f) => f.code_name).join(',')],
  )

  // Clear the form whenever the dialog closes. `useForm` lives at component
  // level, so without this a previous fire's values are still in the fields
  // the next time the dialog opens - and since the product fields decide
  // which product is resolved, a stale facet answer silently changes the
  // result of what looks like a fresh test. Reset on close (an event, not an
  // effect).
  function handleOpenChange(next: boolean) {
    if (!next) formRef.current?.resetForm()
    setOpen(next)
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        title={t('testVa.triggerTitle')}
        className="gap-1.5 border-dashed text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]"
      >
        <FlaskConical size={14} />
        {t('testVa.trigger')}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('testVa.title')}</DialogTitle>
            <DialogDescription>{t('testVa.description')}</DialogDescription>
          </DialogHeader>

          <Form className="flex flex-col gap-4 py-2" />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="test-va-form" disabled={isPending}>
              {isPending ? t('testVa.creating') : t('testVa.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
